import React from 'react';
import * as Sentry from '@sentry/react';
import { telemetryIsPrivate, enterPrivateTelemetry, containsPrivateTelemetry } from '../lib/telemetryPrivacy';

const privatePage = () => typeof window !== 'undefined' && (window.location.pathname.startsWith('/ucw-sys-') || /^\/(?:hosts|worker|after-stay)(?:\/|$)/.test(window.location.pathname));
const privateApi = /\/api\/(?:worker\/mod08(?:\/|\?|$)|admin\/(?:response-library|governance|communication|mod01|mod03|mod10|mod11)(?:\/|\?|$)|hosts\/(?:inbox|properties|mod01|mod03|mod05|mod06|mod07|mod08|concierge)(?:\/|\?|$)|guest\/(?:mod01|mod03|mod05|mod06)(?:\/|\?|$))/;
const privatePayload = value => {
  if (containsPrivateTelemetry(value)) { enterPrivateTelemetry(); return true; }
  return false;
};
const privateEvent = event => privatePayload(event) || telemetryIsPrivate() || privatePage() || event.tags?.privacy_scope === 'response-library' || privateApi.test(event.request?.url || '') || privateApi.test(event.transaction || '');

/**
 * Initialize Sentry on the browser side.
 * No-op when REACT_APP_SENTRY_DSN is missing (dev / preview without DSN).
 */
export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn || telemetryIsPrivate() || privatePage()) return;

  // Tag prod vs preview by hostname so we can filter in the Sentry UI.
  const env =
    typeof window !== 'undefined' && window.location.hostname.endsWith('homesbyli.com')
      ? 'production'
      : 'preview';

  Sentry.init({
    dsn,
    environment: env,
    sendClientReports: false,
    transport: options => {
      const transport = Sentry.makeFetchTransport(options);
      return {
        send: envelope => privatePayload(envelope) || telemetryIsPrivate() || privatePage() ? Promise.resolve({ statusCode: 204 }) : transport.send(envelope),
        flush: timeout => transport.flush(timeout),
      };
    },
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0, // No replay buffer spanning a private-workspace transition.
    sendDefaultPii: false,
    beforeSend: event => privateEvent(event) ? null : event,
    beforeSendTransaction: event => privateEvent(event) ? null : event,
    beforeSendSpan: span => { privatePayload(span); return span; },
    beforeBreadcrumb: breadcrumb => privatePayload(breadcrumb) || telemetryIsPrivate() || privatePage() || privateApi.test(breadcrumb.data?.url || '') ? null : breadcrumb,
    // Drop noisy / non-actionable browser errors.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'Network Error',
      /Loading chunk \d+ failed/,
    ],
  });
}

function FallbackUI({ resetError }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-lg shadow-sm p-8 text-center">
        <h1 className="text-2xl font-semibold text-gray-900 mb-3">
          Something went wrong
        </h1>
        <p className="text-gray-600 mb-6">
          Try again, or head back to the homepage. Private workspace details are not sent to browser telemetry.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={resetError}
            className="px-4 py-2 rounded-md bg-rose-500 hover:bg-rose-600 text-white text-sm font-medium transition-colors"
            data-testid="error-boundary-retry"
          >
            Try again
          </button>
          <a
            href="/"
            className="px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium transition-colors"
            data-testid="error-boundary-home"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const SentryErrorBoundary = ({ children }) => (
  <Sentry.ErrorBoundary fallback={FallbackUI}>{children}</Sentry.ErrorBoundary>
);
